import { CaseSensitive, ChevronDown, ChevronUp, Clock, HardDrive } from '../../../../icons';
import { tChat, type ChatI18nKey } from '../../../../lib/chat-i18n';
import { DEFAULT_SORT_DIR } from '../../../../store/chatMediaStore';
import type { MediaSortDir, MediaSortKey } from '../../../../lib/attachment-groups';

/**
 * 아이콘 정렬 컨트롤 (chat-media-date-groups — 2026-08-07).
 *
 * 사용자 지정: **정렬은 아이콘으로.** 셀렉트/드롭다운 없이 28px 아이콘 토글 3개를 쓴다.
 *   날짜(Clock) · 이름(CaseSensitive) · 크기(HardDrive)
 * 활성 버튼을 다시 누르면 방향이 뒤집히고, 방향은 우하단 chevron 첨자로 표시한다.
 *
 * ⚠ 아이콘뿐이라 **title/aria-label 이 유일한 의미 전달 경로**다 — 세 언어 모두 "현재 상태 + 누르면
 * 무엇이 되는지" 를 문장으로 담는다(`chat.media.sort.<key>_<dir>`).
 *
 * 220px 폭 사이드바에서 Combobox(min-width 140px)를 쓸 공간이 없다는 점, 그리고 레포 게이트
 * `check:no-native-select`(신규 native select 금지)를 자연히 만족한다는 점도 이 선택의 근거다.
 */

const SORT_ICON = {
  date: Clock,
  name: CaseSensitive,
  size: HardDrive,
} as const;

const SORT_ORDER: readonly MediaSortKey[] = ['date', 'name', 'size'];

function sortLabel(key: MediaSortKey, dir: MediaSortDir): string {
  return tChat(`chat.media.sort.${key}_${dir}` as ChatI18nKey);
}

interface MediaSortButtonsProps {
  sortKey: MediaSortKey;
  sortDir: MediaSortDir;
  onToggle: (key: MediaSortKey) => void;
}

export function MediaSortButtons({ sortKey, sortDir, onToggle }: MediaSortButtonsProps) {
  return (
    <div className="chat-media-sort-group" role="group" aria-label={tChat('chat.media.sort_aria')}>
      {SORT_ORDER.map((key) => {
        const Icon = SORT_ICON[key];
        const active = sortKey === key;
        // 비활성 버튼의 툴팁은 "누르면 적용될" 그 축의 기본 방향을 설명한다.
        const label = sortLabel(key, active ? sortDir : DEFAULT_SORT_DIR[key]);
        const Dir = active && sortDir === 'asc' ? ChevronUp : ChevronDown;
        return (
          <button
            key={key}
            type="button"
            className={`chat-media-sort-btn${active ? ' is-active' : ''}`}
            onClick={() => onToggle(key)}
            aria-pressed={active}
            aria-label={label}
            title={label}
          >
            <Icon size={14} aria-hidden />
            <Dir size={10} className="chat-media-sort-dir" aria-hidden />
          </button>
        );
      })}
    </div>
  );
}
